import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TutorReportsHelperService } from '../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { SearchPlacementResult } from '../../_models/placement-search/search-placement-result.model';
import { SessionOverviewHelperService } from '../../_helpers/session-overview-helper/session-overview-helper.service';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SearchPlacementRequest } from '../../_models/placement-search/search-placement-request.model';
import { PlacementStudentV2 } from '../../_models';
import { PlacementService } from '../../_services';
import { OverviewClientFilters, OverviewEncryptedPayload } from '../../_models/session-overview/overview-encrypted-payload.model';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import * as CryptoJS from 'crypto-js';
import { SessionUserHashManagerService } from '../../_services/session-user-hash-manager/session-user-hash-manager.service';
import { CookieService } from 'ngx-cookie-service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { MatDialog } from '@angular/material/dialog';
import { StudentGroupMaintenanceComponent } from '../../maintenance/student-group-maintenance/student-group-maintenance.component';

@Component({
    selector: 'session-overview-placement-main',
    templateUrl: './session-overview-placement-main.component.html',
    styleUrls: ['./session-overview-placement-main.component.css'],
    standalone: false
})
export class SessionOverviewPlacementMainComponent implements OnInit, OnDestroy {
  @Output() toggleFullscreen = new EventEmitter<boolean>();
  
  public bookingSetupViewPermission: boolean = true;
  public tutorReportsViewPermission: boolean = true;

  public sessionFilterText: string;
  public sessionPaginatorPage: number = 1;
  public sessionItemsPerPage: number = 3;

  public hideInternalControls: boolean = false;
  
  private unsubscribe: Subject<void> = new Subject<void>();

  private mobileOverwriteViewState: string = (window.innerWidth - 16) < 720 ? 'less' : 'greater';
  @HostListener('window:resize')
  checkingWidthForSliderShowOverwrite() {
    this.sessionOverviewHelper.mobileOverwrite = (window.innerWidth - 16) < 720;

    let state = (window.innerWidth - 16) < 720 ? 'less' : 'greater';
    if (state != this.mobileOverwriteViewState) {
      this.mobileOverwriteViewState = state;
      this.manageSessionListMobileOverwriteViewState();
    }
  }
  constructor(
    public themeService: ThemeService,
    public sessionOverviewHelper: SessionOverviewHelperService,
    private tutorReportsHelperService: TutorReportsHelperService,
    private router: Router,
    private featureHelperService: FeatureHelperService,
    private route: ActivatedRoute,
    private successModalHelper: SuccessModalHelperService,
    private placementService: PlacementService,
    private userHashManager: SessionUserHashManagerService,
    private cookieService: CookieService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private dialog: MatDialog,
    private featureSwitches: FeatureSwitchService
    ) {
      this.sessionOverviewHelper.mobileOverwrite = (window.innerWidth - 16) < 720;
      this.route.params.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
        const parsedUrl = new URL(window.location.href);
        if (parsedUrl.pathname.indexOf("overview-fullscreen") > 0) {
          if (!params['hash']) {
            this.router.navigate(['/login']);
          } else {

            this.themeService.setActiveTheme('LEAP');

            this.hideInternalControls = true;
            this.decryptFullscreenHash(params['hash']);
          }
        }
      });
  }

  ngOnInit() {
    let featureList = new Array<string>();
    featureList.push(
    "SessionOverviewView",
    "BookingSetupView",
    "TutorReportsView");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);
    this.bookingSetupViewPermission = currentUserFeatures.find(f => f.feature == "BookingSetupView").enabled;
    this.tutorReportsViewPermission = currentUserFeatures.find(f => f.feature == "TutorReportsView").enabled;

    if (!this.hideInternalControls) {
      if (!currentUserFeatures.filter(f => f.feature === "SessionOverviewView")[0].enabled) {
        console.error("User does not have the required permissions to view the overview screen");
        this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname }});
        this.authenticationStateHelper.unauthorizedToken = true;
      }
    }

    this.sessionOverviewHelper.sessionsChangedWatcher().pipe(takeUntil(this.unsubscribe)).subscribe((changed: boolean) => {
      if (changed) {
        this.sessionFilterText = "";
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public sessionPaginationTrigger(event: any): void {
    this.sessionPaginatorPage = event;
    this.manageSessionListMobileOverwriteViewState();
  }

  public filterSessions(event: any): void {
    var inp = event.target.value.toLowerCase();
    if (/[a-zA-Z0-9_ ]/.test(inp)) {
      let temp = this.sessionOverviewHelper.sessionsFilterHolder.slice();
      temp = temp.filter((item: SearchPlacementResult) => item.clientName.toLowerCase().includes(inp)
        || item.session.toLowerCase().includes(inp)
        || item.yearGroup.toLowerCase().includes(inp)
        || item.clientName.toLowerCase().includes(inp)
        || item.bookingId.toLowerCase().includes(inp)
        );

      this.sessionOverviewHelper.sessions = temp;
    } else if (inp == "") {
      this.sessionOverviewHelper.sessions = this.sessionOverviewHelper.sessionsFilterHolder.slice();
    }
  }

  public clearSessionFilter(): void {
    this.sessionFilterText = "";
    this.sessionOverviewHelper.sessions = this.sessionOverviewHelper.sessionsFilterHolder.slice();
    this.sessionPaginationTrigger(1);
  }

  public toggleSlider(section: string, session: SearchPlacementResult): void {

    let index = this.sessionOverviewHelper.sessions.findIndex(s => s.bookingId == session.bookingId);

    this.sessionOverviewHelper.sessions[index].showSessionSlider = section == "session";
    this.sessionOverviewHelper.sessions[index].showInfo = section == "info";
  }

  public navigatePlacement(session: SearchPlacementResult, destination: string): void {
    if (destination == 'reports') {
      this.tutorReportsHelperService.setCommonDataBeforeNavigate(+session.bookingId, session.startDate, session.endDate);
      setTimeout(() => {
        this.router.navigate(["/tutorreports", +session.bookingId]);
      }, 200);
    } else if(destination == 'setup') {
      this.dialog.open(StudentGroupMaintenanceComponent, {
        data: { placementId: session.bookingId, externalView: false },
        panelClass: 'group-maintenance-dialog'
      });
    }
  }

  public scrollRenderedBlocks($event: any, scrollMe: any): void {
    scrollMe.scrollTo({ left: (scrollMe.scrollWidth), behavior: 'smooth' });
  }

  public togglePlacementFullscreenView(): void {
    this.toggleFullscreen.emit(true);
  }

  private manageSessionListMobileOverwriteViewState(): void {
    if (this.sessionOverviewHelper.sessions != null) {
      this.sessionOverviewHelper.sessions.forEach(item => {
        if (this.sessionOverviewHelper.mobileOverwrite) {
          item.showInfo = true;
          item.showSessionSlider = false;
        } else {
          item.showInfo = false;
          item.showSessionSlider = false;
        }
      });
    }
  }
  
  private searchPlacementsForFullscreen(request: SearchPlacementRequest, filters: OverviewClientFilters = null): void {
    this.placementService.searchPlacements(request).subscribe((response: SearchPlacementResult[]) => {

      if (response == null) {
        this.sessionOverviewHelper.sessions = new Array<SearchPlacementResult>(); 
        this.sessionOverviewHelper.sessionsFilterHolder = new Array<SearchPlacementResult>();
      } else {
        const shown = this.sessionOverviewHelper.applyClientFilters(response, filters);
        this.sessionOverviewHelper.sessions = shown;
        this.sessionOverviewHelper.sessionsFilterHolder = shown;
      }

      this.sessionOverviewHelper.sessions.forEach(session => {
        let emptySpaceCount = session.maxGroupSize - session.students.length;
        if (Math.sign(emptySpaceCount) != -1) {
          for (let i = 0; i < emptySpaceCount; i++) {
            let emptyStudent = new PlacementStudentV2();
            emptyStudent.studentFirstName = "Unassigned Slot";
            emptyStudent.studentLastName = "";
            emptyStudent.studentId = 0;
            session.students.push(emptyStudent);
          }
        }
        if (this.sessionOverviewHelper.mobileOverwrite) {
          session.showInfo = true;
          session.showSessionSlider = false;
        } else {
          session.showInfo = false;
          session.showSessionSlider = false;
        }
      });

      if (this.hideInternalControls) {
        this.sessionItemsPerPage = response.length;
      }

      
    }, error => {
      this.errorModalHelper.show(error.message, error);
    });
        
  }

  private decryptFullscreenHash(guid: any): void {

    let cookie = this.cookieService.get(guid);

    let decryptedHash = this.decryptPayload(cookie);

    // unsuccessful decrypt route to login
    if (decryptedHash == "") {
      this.router.navigate(['/login']);
    }
    
    let payload: OverviewEncryptedPayload = JSON.parse(decryptedHash);

    if (this.userHashManager.validateHash(payload.userHash)) {
      // The hand-off is honoured only as far as filters.session-overview allows on this page load:
      // off, the request goes out in the pre-filter shape and no client-side filter applies.
      this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersSessionOverview).pipe(takeUntil(this.unsubscribe)).subscribe(enabled => {
        if (!enabled) {
          delete payload.request.companyIds;
        }
        this.searchPlacementsForFullscreen(payload.request, enabled ? payload.filters ?? null : null);
      });
    } else {
      this.router.navigate(['/login']);
    }
  }

  private decryptPayload(encryptedData: string): string {
    try {
      var iv = CryptoJS.enc.Hex.parse("e84ad660c4721ae0e84ad660c4721ae0");
      let user = JSON.parse(localStorage.getItem('currentUser'));
      var Pass = CryptoJS.enc.Utf8.parse(user.firstName);

      var Salt = CryptoJS.enc.Utf8.parse(user.username);

      var key128Bits1000Iterations = CryptoJS.PBKDF2(Pass.toString(CryptoJS.enc.Utf8), Salt, { keySize: 128 / 32, iterations: 1000 });

      var cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Base64.parse(encryptedData)
      });

      var decrypted = CryptoJS.AES.decrypt(cipherParams, key128Bits1000Iterations, { mode: CryptoJS.mode.CBC, iv: iv, padding: CryptoJS.pad.Pkcs7 });

      return decrypted.toString(CryptoJS.enc.Utf8);
    }
    //Malformed UTF Data due to incorrect password
    catch (err) {
      return "";
    }
  }

}
